import { useEffect } from "react";
import { Link } from "react-router-dom";
import LogoUrl from "@/components/brand/logo";
import ArchDiagram from "@/assets/diagram-architecture.svg";
import MemoryDiagram from "@/assets/diagram-memory.svg";
import { Button } from "@/components/ui/button";

const stats = [
  { label: "Target token reduction", value: "≥50%" },
  { label: "Memory tiers", value: "STM · CWM · LTM" },
  { label: "Agents", value: "Orchestrator + 4" },
];

const agents = [
  {
    title: "Orchestrator",
    desc:
      "Owns the global objective, schedules agents, triggers compression, and injects retrieved memory.",
  },
  {
    title: "Planner (Worker)",
    desc: "Produces concrete outputs and action plans for each step.",
  },
  {
    title: "Critic (Worker)",
    desc: "Checks consistency, missing constraints, and risk before output is finalized.",
  },
  {
    title: "Compression Agent",
    desc:
      "Distills STM + prior CWM into structured memory with supersession and uncertainty tracking.",
  },
  {
    title: "Retrieval Agent",
    desc:
      "Selects the minimal relevant memory slice before every agent run to prevent bloat.",
  },
];

const triggers = [
  "Token budget threshold exceeded",
  "Fixed interval (e.g., every 4 steps)",
  "Explicit /compress request",
  "Phase change or agent handoff",
];

const metrics = [
  "baseline_tokens: objective + full transcript",
  "injected_tokens: objective + retrieved memory + STM tail",
  "reduction_pct: (baseline - injected) / baseline",
  "dropped[]: explicit loss analysis with reasons",
];

export default function LandingPage() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="min-h-screen bg-[radial-gradient(60%_60%_at_20%_15%,rgba(124,58,237,0.16),transparent),radial-gradient(55%_55%_at_80%_25%,rgba(20,184,166,0.14),transparent),radial-gradient(45%_45%_at_55%_80%,rgba(244,114,182,0.12),transparent)] text-slate-100">

      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#05070c]/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img
              src={LogoUrl}
              alt="XtoInfiniti logo"
              className="h-12 w-12 rounded-2xl object-cover"
            />
            <div>
              <div className="font-display text-xl tracking-tight">XtoInfiniti</div>
              <div className="text-xs text-slate-400">Context Compression Engine</div>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm text-slate-300 lg:flex">
            <a className="hover:text-white" href="#architecture">
              Architecture
            </a>
            <a className="hover:text-white" href="#agents">
              Agents
            </a>
            <a className="hover:text-white" href="#memory">
              Memory
            </a>
            <a className="hover:text-white" href="#metrics">
              Metrics
            </a>
            <Link className="hover:text-white" to="/mvp">
              Access
            </Link>
          </nav>
          <Button
            asChild
            className="rounded-full bg-gradient-to-r from-emerald-300 via-sky-300 to-blue-300 text-slate-900 shadow-lg shadow-sky-500/20 hover:opacity-90"
          >
            <Link to="/mvp">Access Infiniti</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6">
        <section className="grid gap-12 py-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-300">
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)] animate-pulse"
              />
              XtoInfiniti · Multi-Agent Context Engine
            </div>
            <h1 className="font-display text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">
              XtoInfiniti builds an effectively infinite context window for real work.
            </h1>
            <p className="mt-6 text-lg text-slate-300">
              This is core infrastructure, not a chatbot. XtoInfiniti runs a team of
              specialized agents, compresses context without losing constraints, and
              rehydrates only what matters so long-horizon tasks stay coherent.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-white text-slate-900 hover:bg-slate-100"
              >
                <Link to="/mvp">Access Infiniti</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full border-white/20 text-white hover:bg-white/10"
              >
                <a href="#architecture">View Architecture</a>
              </Button>
            </div>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4"
                >
                  <div className="text-xs text-slate-400">{stat.label}</div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/10 via-white/5 to-transparent p-8">
            <div className="rounded-2xl border border-white/10 bg-black/70 p-6">
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Engine Snapshot
              </div>
              <div className="mt-4 space-y-3 text-sm text-slate-300">
                <div>STM: verbatim recent messages (windowed)</div>
                <div>CWM: structured memory with supersession + confidence</div>
                <div>LTM: stable knowledge retrieved on demand</div>
                <div>Determinism: temperature=0 + strict JSON schemas</div>
              </div>
              <div className="mt-6 rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-xs text-slate-400">Compression objective</div>
                <div className="mt-2 text-base text-white">
                  Preserve constraints and decisions while cutting context size.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="architecture" className="py-16">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <h2 className="font-display text-3xl text-white">System Architecture</h2>
              <p className="mt-4 text-slate-300">
                Each run is a single long-horizon task. The Orchestrator drives the
                loop, calls Retrieval before every agent run, and triggers Compression
                on thresholds or phase changes. Every step is logged for inspection.
              </p>
              <div className="mt-6 grid gap-3 text-sm text-slate-300">
                <div>Run object owns objective, config, state, and memory pointers.</div>
                <div>Event log captures every agent input/output deterministically.</div>
                <div>Snapshots written to disk after each compression cycle.</div>
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <img
                src={ArchDiagram}
                alt="Architecture diagram of agents and memory flow"
                className="w-full"
              />
            </div>
          </div>
        </section>

        <section id="agents" className="py-16">
          <div className="flex flex-col gap-8">
            <div>
              <h2 className="font-display text-3xl text-white">Agent Ensemble</h2>
              <p className="mt-4 text-slate-300">
                Five cooperating roles keep long tasks aligned. The system never
                truncates naively; it compresses semantically and injects only what
                is relevant for the next step.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((agent) => (
                <div
                  key={agent.title}
                  className="rounded-2xl border border-white/10 bg-white/5 p-6"
                >
                  <div className="text-sm uppercase tracking-widest text-slate-400">
                    {agent.title}
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{agent.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="memory" className="py-16">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <img
                src={MemoryDiagram}
                alt="Memory tiers diagram"
                className="w-full"
              />
            </div>
            <div>
              <h2 className="font-display text-3xl text-white">Three Memory Tiers</h2>
              <p className="mt-4 text-slate-300">
                STM stores verbatim recent messages. CWM stores structured memory with
                facts, decisions, constraints, assumptions, definitions, and open loops.
                LTM holds stable knowledge promoted over time.
              </p>
              <div className="mt-6 grid gap-3 text-sm text-slate-300">
                <div>Supersession links preserve changes without losing history.</div>
                <div>External storage in MongoDB keeps memory inspectable.</div>
                <div>Snapshots written to disk on every compression cycle.</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-display text-2xl text-white">Compression Triggers</h3>
              <div className="mt-4 grid gap-3 text-sm text-slate-300">
                {triggers.map((item) => (
                  <div key={item}>• {item}</div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-display text-2xl text-white">Context Rehydration</h3>
              <p className="mt-4 text-sm text-slate-300">
                Retrieval selects the minimal subset of CWM and LTM. Injection order is
                strict: constraints, definitions, decisions, then summaries/open loops.
                This keeps prompts tight without losing invariants.
              </p>
            </div>
          </div>
        </section>

        <section id="metrics" className="py-16">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <h2 className="font-display text-3xl text-white">Evaluation Metrics</h2>
              <p className="mt-4 text-slate-300">
                Token usage is measured before and after compression. Loss analysis is
                explicit, and deterministic fallback mode provides full reproducibility.
              </p>
              <div className="mt-6 grid gap-3 text-sm text-slate-300">
                {metrics.map((item) => (
                  <div key={item}>• {item}</div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-black/70 p-6">
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Token Estimation
              </div>
              <div className="mt-4 font-mono text-sm text-slate-200">
                est_tokens ≈ ceil(len(text) / 4)
              </div>
              <div className="mt-6 text-xs text-slate-400">Determinism modes</div>
              <div className="mt-2 space-y-2 text-sm text-slate-300">
                <div>Best-effort deterministic: temperature=0, strict JSON.</div>
                <div>Strict deterministic: rule-based retrieval/compression.</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-display text-2xl text-white">API Surface</h3>
              <pre className="mt-4 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-white/10 bg-black/60 p-4 text-xs text-slate-200">
{`POST /api/runs
POST /api/runs/{run_id}/step
POST /api/runs/{run_id}/compress
GET  /api/runs/{run_id}/memory
GET  /api/runs/{run_id}/events
GET  /api/runs/{run_id}/snapshots/latest
POST /api/demo/run`}
              </pre>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
              <h3 className="font-display text-2xl text-white">Demo Scenarios</h3>
              <div className="mt-4 space-y-4 text-sm text-slate-300">
                <div>
                  Scenario A: long PRD, API, schema, and metrics build-out with
                  multiple compression cycles.
                </div>
                <div>
                  Scenario C: self-demo that iterates on the engine itself with
                  change requests mid-run.
                </div>
              </div>
              <div className="mt-6 rounded-xl border border-white/10 bg-black/60 p-4 text-xs text-slate-200">
                Run demo via UI or POST /api/demo/run
              </div>
            </div>
          </div>
        </section>

        <section id="access" className="py-20">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-white/10 via-white/5 to-transparent p-10">
            <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
              <div>
                <h2 className="font-display text-3xl text-white">Access Infiniti</h2>
                <p className="mt-4 text-slate-300">
                  Launch the engine locally and inspect every step. The stack is
                  FastAPI + MongoDB + React + Tailwind, with OpenRouter-compatible
                  LLM routing.
                </p>
                <div className="mt-6 grid gap-3 text-sm text-slate-300">
                  <div>Backend: uvicorn server:app --host 0.0.0.0 --port 8001</div>
                  <div>Frontend: npm start</div>
                  <div>Open UI at http://localhost:3000</div>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-gradient-to-r from-emerald-300 via-sky-300 to-blue-300 text-slate-900"
                >
                  <Link to="/mvp">Access Infiniti</Link>
                </Button>
                <div className="text-xs text-slate-400">
                  Built for deterministic inspection, long-horizon tasks, and
                  compression you can audit.
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
